/**
 * Offline Sync — Push Engine (S6-03)
 *
 * Sibling to the visit-only `flush()` in offline-sync.service.ts. This engine
 * pushes every entity type the spec mandates, **one record at a time**, in a
 * fixed order so the server never sees a child before its parent:
 *
 *   patients → allergies → doctors → visits → vitals → appointments → prescriptions
 *
 * Within an entity type, records are pushed ascending by client_timestamp.
 *
 * Failure / chain semantics:
 *   • transport error or 5xx — likely the network is down; stop this entity
 *     type's chain so we don't burn cycles on calls that will all fail.
 *   • 4xx (non-409) failed CREATE — the entity will never reach the server in
 *     this cycle. Add its entity_id to `orphanedIds` so any later child
 *     record (visits referencing this patient, etc.) is skipped to avoid a
 *     guaranteed FK violation server-side.
 *   • 409 conflict or 4xx update — record-local; leave the chain alone.
 *
 * Cadence:
 *   • setInterval(SYNC_PUSH_INTERVAL_MS), guarded by navigator.onLine
 *   • window 'online' event triggers an immediate cycle
 *   • single-flight: concurrent calls are coalesced
 *
 * Conflicts (HTTP 409): the row is moved to status='conflict' with the
 * server-side snapshot stashed in `server_snapshot`. The UI's conflict
 * modal renders local vs server and calls resolveKeepMine / resolveUseServer.
 *
 * On a successful push, the engine ALSO marks the entity table row as
 * sync_status='synced' so UI components reading from patients/visits/etc.
 * reflect the freshly-synced state. Without this, rows would appear
 * perpetually "pending" in the UI even after a successful push.
 *
 * Logging is PHI-safe — only non-PHI metadata is passed to the logger.
 */

import type { Table } from "dexie";

import { getDek } from "../db/crypto";
import { apiFetch } from "./api.client";
import { ClinicScopeChangedError, isClinicScopeStale } from "../utils/active-clinic";

import {
  API_BASE_URL,
  SYNC_DONE_RETENTION_MS,
  SYNC_FETCH_TIMEOUT_MS,
  SYNC_MAX_RETRIES,
  SYNC_PUSH_INTERVAL_MS,
  SYNC_PUSH_ORDER,
} from "../constants";
import { db } from "../db/schema";
import type {
  EntityType,
  SyncQueueRecord,
} from "../db/db.types";
import { unwrapSyncQueuePayload, wrapSyncQueuePayload } from "../db/encryption-bridge";
import { logger } from "../utils/logger";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PushCycleResult {
  attempted: number;
  synced:    number;
  conflicts: number;
  failed:    number;
  /** Records skipped due to FK orphan or network-broken chain. */
  skipped:   number;
}

// Server 409 body shape (inside `detail` wrapper):
// { "detail": { "error": "version_conflict", "server_version": 4, "server_data": {...} } }
interface ServerConflict {
  error?:          string;
  server_version?: number;
  server_data?:    Record<string, unknown>;
}

type PushOutcome = "synced" | "conflict" | "failed" | "retry";

// Foreign-key references in `data` payloads. Used to skip child records when
// a parent CREATE failed in the same cycle (the parent doesn't exist server-
// side, so the child push would FK-violate).
//
// Add new entity types here as the schema grows.
const FK_REFS: Partial<Record<EntityType, { field: string; refType: EntityType }[]>> = {
  patient:      [],
  doctor:       [],
  allergy:      [{ field: "patient_id", refType: "patient" }],
  visit:        [
    { field: "patient_id", refType: "patient" },
    { field: "doctor_id",  refType: "doctor"  },
  ],
  vitals:       [{ field: "visit_id", refType: "visit" }],
  appointment:  [
    { field: "patient_id", refType: "patient" },
    { field: "doctor_id",  refType: "doctor"  },
  ],
  prescription: [{ field: "visit_id", refType: "visit" }],
};

// ─── Module state ────────────────────────────────────────────────────────────

let inFlight: Promise<PushCycleResult> | null = null;
const listeners = new Set<() => void>();

/** Subscribe to every cycle / state change. Returns an unsubscribe fn. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function notify(): void {
  // Listeners are React state setters — keep this synchronous and cheap.
  listeners.forEach(l => { try { l(); } catch { /* listener errors must not break the cycle */ } });
}

// ─── Public API ──────────────────────────────────────────────────────────────

/** Run one push cycle. Concurrent callers receive the same in-flight promise. */
export function pushOnce(): Promise<PushCycleResult> {
  if (inFlight) return inFlight;
  inFlight = doPushCycle().finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * Start the background loop. Returns a teardown function. Idempotent at the
 * call-site (caller is responsible for not double-mounting).
 *
 * The loop:
 *   • fires every SYNC_PUSH_INTERVAL_MS when navigator.onLine is true
 *   • fires immediately on window 'online'
 *   • fires once at startup if already online (catches refreshes with queued items)
 */
export function startPushLoop(): () => void {
  const tick = () => {
    if (!navigator.onLine) return;
    pushOnce().catch(() => { /* errors are persisted in the queue, never re-thrown */ });
  };

  const intervalId = window.setInterval(tick, SYNC_PUSH_INTERVAL_MS);
  window.addEventListener("online", tick);

  // Catch the "app launched online with queue items" case immediately.
  tick();

  // Prune `done` rows once at startup so PHI-bearing payloads don't accumulate
  // in IndexedDB indefinitely. Safe to run in the background — non-fatal.
  pruneDone().catch(() => { /* non-fatal */ });

  return () => {
    window.clearInterval(intervalId);
    window.removeEventListener("online", tick);
  };
}

/** Remove `done` sync_queue rows older than SYNC_DONE_RETENTION_MS. */
async function pruneDone(): Promise<void> {
  const cutoff = Date.now() - SYNC_DONE_RETENTION_MS;
  const stale = await db.sync_queue
    .where("status").equals("done")
    .filter(r => new Date(r.client_timestamp).getTime() < cutoff)
    .primaryKeys();
  if (stale.length > 0) {
    await db.sync_queue.bulkDelete(stale);
    logger.warn("sync-push: pruned done items", { count: stale.length });
  }
}

// ─── Conflict resolution (called by ConflictModal) ───────────────────────────

/**
 * "Keep Mine" — re-push the local record with force=true to overwrite the
 * server. If the server has changed AGAIN since the user opened the modal,
 * we surface a fresh conflict back to the queue.
 */
export async function resolveKeepMine(queueId: string): Promise<void> {
  const raw = await db.sync_queue.get(queueId);
  if (!raw || raw.status !== "conflict") return;
  const rec = await unwrapSyncQueuePayload(raw as unknown as Record<string, unknown>);

  await db.sync_queue.update(queueId, {
    status:          "syncing",
    server_snapshot: undefined,
    last_error:      undefined,
  });
  notify();

  let resp: Response;
  try {
    resp = await postOne(rec, /* force */ true);
  } catch (err) {
    await onTransportError(rec, err);
    return;
  }

  if (resp.ok) {
    await markSynced(rec);
    return;
  }
  if (resp.status === 409) {
    const data = await readConflictBody(resp);
    await db.sync_queue.update(queueId, {
      status:          "conflict",
      server_snapshot: data?.server_data,
      last_error:      data?.error ?? "Conflict",
    });
    notify();
    return;
  }
  await db.sync_queue.update(queueId, {
    status:     "failed",
    last_error: `HTTP ${resp.status}`,
  });
  notify();
}

/**
 * "Use Server Version" — discard the local mutation AND overwrite the local
 * Dexie row with the server snapshot we cached at 409 time. Without the
 * overwrite the user would still see their stale local edits in the UI; the
 * pull engine would only catch up on its next cycle.
 */
export async function resolveUseServer(queueId: string): Promise<void> {
  const rec = await db.sync_queue.get(queueId);
  if (!rec) return;

  if (rec.server_snapshot && rec.entity_id) {
    const table = entityTableFor(rec.entity_type);
    if (table) {
      try {
        const serverVersion = typeof rec.server_snapshot.version === "number"
          ? rec.server_snapshot.version
          : 1;
        // Cast: we trust server_snapshot to be the full entity shape (the
        // server returned it for this row at 409 time). Dexie writes by PK.
        await table.put({
          ...rec.server_snapshot,
          sync_status:      "synced",
          version:          serverVersion,
          local_updated_at: new Date().toISOString(),
        });
      } catch (err) {
        // Defensive: don't block the queue cleanup if the local write fails.
        logger.warn("sync-push: useServer overwrite failed", { entity_type: rec.entity_type });
      }
    }
  }

  await db.sync_queue.delete(queueId);
  // Let any open pages re-fetch their server state.
  window.dispatchEvent(new CustomEvent("sync-conflict-resolved", {
    detail: { entity_type: rec.entity_type, entity_id: rec.entity_id },
  }));
  notify();
}

// ─── Internals ───────────────────────────────────────────────────────────────

async function doPushCycle(): Promise<PushCycleResult> {
  // Another tab switched this browser's clinic. The shared queue now belongs to
  // that tab's switch — it drains it under the right clinic — so this tab must
  // not push it under its own stale header, or even reset its statuses.
  if (isClinicScopeStale()) {
    return { attempted: 0, synced: 0, conflicts: 0, failed: 0, skipped: 0 };
  }

  // Ensure the DEK is in memory before touching any encrypted queue payloads.
  // The idle-wipe timer clears it after 15 min of inactivity; the push engine
  // runs on a background timer and never triggers the activity reset, so the
  // DEK may be gone by the time a push fires. getDek() transparently re-fetches
  // the wrapped key from the server if needed (Amplify session is still valid).
  // If the server is unreachable (offline), this throws and we bail — correct,
  // since we can't decrypt payloads without the key anyway.
  try {
    await getDek();
  } catch {
    return { attempted: 0, synced: 0, conflicts: 0, failed: 0, skipped: 0 };
  }

  // Recover entries left in "syncing" from a previous interrupted cycle
  // (page reload, tab close, browser crash). They will never be retried
  // otherwise because pendingFor() only picks up "pending" rows.
  await db.sync_queue
    .where("status").equals("syncing")
    .modify({ status: "pending" })
    .catch(() => { /* non-fatal */ });

  if (!deviceGateFailuresRequeued) {
    deviceGateFailuresRequeued = true;
    await requeueDeviceGateFailures().catch(() => { /* non-fatal */ });
  }

  const result: PushCycleResult = {
    attempted: 0, synced: 0, conflicts: 0, failed: 0, skipped: 0,
  };

  // entity_ids whose CREATE failed this cycle — children that reference them
  // will be skipped to avoid a guaranteed server-side FK violation.
  const orphanedIds: Partial<Record<EntityType, Set<string>>> = {};

  // Once any entity type hits a transport/5xx error, the backend is unreachable
  // for this entire cycle. Skip all remaining entity types entirely — including
  // their pre-checks (e.g. GET /visits before prescriptions). In a PWA build the
  // service worker intercepts failing GETs and returns a synthetic 404 instead of
  // throwing, which would cause the push engine to permanently fail prescriptions
  // whose visits actually exist server-side.
  let cycleNetworkBroken = false;

  for (const entityType of SYNC_PUSH_ORDER) {
    const rows = await pendingFor(entityType);
    if (rows.length === 0) continue;

    // If a prior entity type already hit a transport error, skip this type
    // without touching the rows (they stay "pending" for the next cycle).
    if (cycleNetworkBroken) {
      result.skipped += rows.length;
      continue;
    }

    let networkBroken = false;

    for (const row of rows) {
      // Skip child if its parent failed earlier in this cycle.
      if (referencesOrphan(row, orphanedIds)) {
        result.skipped += 1;
        continue;
      }
      // Skip if a transport/5xx earlier in this entity type told us the
      // network is unhappy — no point hammering it with more requests.
      if (networkBroken) {
        result.skipped += 1;
        continue;
      }

      result.attempted += 1;
      const outcome = await pushRecord(row);

      if (outcome === "synced") {
        result.synced += 1;
      } else if (outcome === "conflict") {
        result.conflicts += 1;
        // 409 is record-local: the parent EXISTS on the server (just at a
        // different version). Don't block siblings or children.
      } else if (outcome === "failed") {
        result.failed += 1;
        // 4xx on a CREATE means the entity will never appear server-side
        // this cycle — quarantine its id so children get skipped, not 4xx'd.
        if (row.operation === "create" && row.entity_id) {
          (orphanedIds[row.entity_type] ??= new Set()).add(row.entity_id);
        }
      } else /* "retry" — transport or 5xx */ {
        result.failed += 1;
        networkBroken = true;
        cycleNetworkBroken = true;
        if (row.operation === "create" && row.entity_id) {
          (orphanedIds[row.entity_type] ??= new Set()).add(row.entity_id);
        }
      }
    }

    if (networkBroken) {
      logger.warn("sync-push: cycle stopped (network unreachable)", { entity_type: entityType });
    }
  }

  notify();
  return result;
}

let deviceGateFailuresRequeued = false;

// The push used to go out without X-Device-Id, so for every user the device
// gate does not exempt (doctors, receptionists) each replayed write was
// answered 403 device_verification_required and marked failed for good.
// Requeue those, plus the children the FK pre-checks then failed because their
// parent's CREATE had failed. Anything that fails again for a real reason is
// marked failed again, with that reason.
const DEVICE_GATE_ERROR = "device_verification_required";
const PARENT_CREATE_FAILED = /^CREATE for this \w+ failed|offline CREATE permanently failed/;

async function requeueDeviceGateFailures(): Promise<void> {
  const count = await db.sync_queue
    .where("status").equals("failed")
    .filter(r =>
      r.last_error === DEVICE_GATE_ERROR ||
      PARENT_CREATE_FAILED.test(r.last_error ?? ""))
    .modify({ status: "pending", retry_count: 0, last_error: undefined });
  if (count > 0) {
    logger.warn("sync-push: requeued writes failed by the device gate", { count });
  }
}

function referencesOrphan(
  row: SyncQueueRecord,
  orphanedIds: Partial<Record<EntityType, Set<string>>>,
): boolean {
  const refs = FK_REFS[row.entity_type] ?? [];
  for (const ref of refs) {
    const refId = (row.data as Record<string, unknown>)[ref.field];
    if (typeof refId === "string" && orphanedIds[ref.refType]?.has(refId)) return true;
  }
  return false;
}

/**
 * Validate FK references before sending to the server. Returns an error string
 * if any referenced entity still has a temp/local ID that hasn't been remapped
 * to a real server ID (i.e. the entity was synced before the remapping code
 * was in place). Returns null if all FKs look safe.
 *
 * Key invariant: when the server always generates its own UUID (e.g. patients —
 * PatientCreate has no patient_id field), remapQueueReferences patches any
 * still-pending child entries to use the server UUID. If remapping ran, the
 * child's FK value will be the SERVER uuid, and no done entry exists for that
 * server uuid — so this check passes. If remapping never ran (pre-deployment
 * legacy data), the child's FK still holds the TEMP uuid, and a done entry
 * exists for that exact temp uuid — this check blocks with a clear message.
 */
async function validateFKRefs(rec: SyncQueueRecord): Promise<string | null> {
  if (rec.operation !== "create") return null;
  const refs = FK_REFS[rec.entity_type] ?? [];
  const d = (rec.data ?? {}) as Record<string, unknown>;

  for (const ref of refs) {
    const refId = d[ref.field];
    if (typeof refId !== "string") continue;

    // Prescription→visit: skip the done-entry heuristic entirely. The server
    // sometimes assigns the same UUID the client generated (serverId === tempId),
    // which leaves the done entry with entity_id = clientUUID. The heuristic
    // would then falsely flag the prescription as having a stale temp FK when
    // the visit actually exists on the server. The GET /visits pre-check
    // below (lines ~467) handles this accurately — rely on that instead.
    if (rec.entity_type === "prescription" && ref.refType === "visit") continue;

    // A done queue entry for the parent with this same ID means the parent was
    // created offline with a temp UUID. Because the server always generates its
    // own UUID for these entities, the temp UUID doesn't exist server-side.
    // If ID remapping had run, this child's FK would already be the server UUID
    // (not the temp UUID), so this done entry wouldn't match. The match itself
    // proves the child has a stale temp FK — block before the server 500s.
    const doneEntry = await db.sync_queue
      .filter(e => e.entity_type === ref.refType && e.entity_id === refId && e.status === "done")
      .first();

    if (doneEntry) {
      return (
        `This ${rec.entity_type} references a ${ref.refType} that was created ` +
        `offline (temp ID ${refId.slice(0, 8)}…) but the ID was never remapped ` +
        `to the server UUID. Delete this queue entry and re-book using a ` +
        `server-confirmed ${ref.refType}.`
      );
    }

    // If the parent CREATE has been permanently marked "failed" (max retries
    // exhausted while the backend was down), the temp UUID will never reach the
    // server. Pushing this child would produce a guaranteed 404 that burns a
    // retry count and pollutes the logs. Block it and surface a clear message.
    const failedCreate = await db.sync_queue
      .filter(e =>
        e.entity_type === ref.refType &&
        e.entity_id  === refId &&
        e.operation  === "create" &&
        e.status     === "failed"
      )
      .first();

    if (failedCreate) {
      return (
        `This ${rec.entity_type} references a ${ref.refType} whose offline CREATE ` +
        `permanently failed (temp ID ${refId.slice(0, 8)}…). ` +
        `Re-create the ${ref.refType} online to retry sync.`
      );
    }
  }
  return null;
}

async function pendingFor(entityType: EntityType): Promise<SyncQueueRecord[]> {
  const rows = await db.sync_queue
    .where("status").equals("pending")
    .filter(r => r.entity_type === entityType)
    .toArray();
  rows.sort((a, b) => a.client_timestamp.localeCompare(b.client_timestamp));
  return Promise.all(rows.map(r => unwrapSyncQueuePayload(r as unknown as Record<string, unknown>)));
}

async function pushRecord(rec: SyncQueueRecord): Promise<PushOutcome> {
  // Before hitting the server, validate FK references via the done queue-entry
  // heuristic (catches same-session cases).
  const fkError = await validateFKRefs(rec);
  if (fkError) {
    await db.sync_queue.update(rec.id, { status: "failed", last_error: fkError });
    logger.warn("sync-push: FK pre-check failed", { entity_type: rec.entity_type, reason: fkError });
    notify();
    return "failed";
  }

  // If a CREATE for this entity already failed, any UPDATE/DELETE to the same
  // temp entity_id is guaranteed to 404. Mark it failed immediately rather than
  // hammering the server with requests that can never succeed.
  if (rec.operation !== "create" && rec.entity_id) {
    const failedCreate = await db.sync_queue
      .filter(e =>
        e.entity_type === rec.entity_type &&
        e.entity_id  === rec.entity_id   &&
        e.operation  === "create"         &&
        e.status     === "failed"
      )
      .first();
    if (failedCreate) {
      const errMsg = `CREATE for this ${rec.entity_type} failed — re-create the record to retry sync`;
      await db.sync_queue.update(rec.id, { status: "failed", last_error: errMsg });
      logger.warn("sync-push: skipped update after failed create", { entity_type: rec.entity_type });
      notify();
      return "failed";
    }
  }

  // For appointment CREATEs, verify the referenced patient actually exists on
  // the server before attempting the push. Without this, a stale temp UUID
  // (from an offline-created patient synced before ID-remapping was deployed)
  // causes a server-side FK violation → 500. The done-queue heuristic in
  // validateFKRefs cannot catch this once pruneDone has removed the entry (>24h).
  if (rec.entity_type === "appointment" && rec.operation === "create") {
    const d = rec.data as Record<string, unknown>;
    const pid = typeof d.patient_id === "string" ? d.patient_id : null;
    if (pid) {
      let checkResp: Response;
      try {
        // The active-clinic header matters here: checked against the wrong
        // clinic, a valid patient 404s and the appointment is failed for good.
        checkResp = await apiFetch(`${API_BASE_URL}/patients/${pid}`, {
          signal: AbortSignal.timeout(5_000),
        });
      } catch (err) {
        if (err instanceof ClinicScopeChangedError) throw err;
        return await onTransportError(rec, err);
      }
      if (checkResp.status === 404) {
        const errMsg =
          `Patient ${pid.slice(0, 8)}… not found on the server — ` +
          `this appointment references a stale offline ID. ` +
          `Delete this entry and re-book with a server-confirmed patient.`;
        await db.sync_queue.update(rec.id, { status: "failed", last_error: errMsg });
        logger.warn("sync-push: appointment patient 404", { patient_id: pid });
        notify();
        return "failed";
      }
    }
  }

  // For prescriptions (any operation), verify the referenced visit exists on
  // the server before pushing. The done-queue heuristic in validateFKRefs only
  // works within SYNC_DONE_RETENTION_MS (24 h); after pruneDone removes the
  // done entry, stale temp visit_ids slip through and cause guaranteed 404s.
  //
  // IMPORTANT: if this fetch throws (TypeError "Failed to fetch"), propagate it
  // so the caller returns "retry" and sets cycleNetworkBroken. In a PWA build,
  // the service worker intercepts failing requests and returns synthetic 404s
  // instead of throwing — cycleNetworkBroken in doPushCycle prevents reaching
  // this pre-check entirely when the backend is already known-unreachable.
  if (rec.entity_type === "prescription") {
    const d = rec.data as Record<string, unknown>;
    const vid = typeof d.visit_id === "string" ? d.visit_id : null;
    if (vid) {
      let checkResp: Response;
      try {
        checkResp = await apiFetch(`${API_BASE_URL}/visits/${vid}`, {
          signal: AbortSignal.timeout(5_000),
        });
      } catch (err) {
        if (err instanceof ClinicScopeChangedError) throw err;
        // Transport error — backend unreachable. Let onTransportError handle it.
        return await onTransportError(rec, err);
      }
      if (checkResp.status === 404) {
        const errMsg =
          `Visit ${vid.slice(0, 8)}… not found on the server — ` +
          `this prescription references a stale offline visit ID. ` +
          `Re-enter the prescription from the correct visit.`;
        await db.sync_queue.update(rec.id, { status: "failed", last_error: errMsg });
        logger.warn("sync-push: prescription visit 404", { visit_id: vid });
        notify();
        return "failed";
      }
    }
  }

  await db.sync_queue.update(rec.id, { status: "syncing" });

  // Re-read from Dexie: if an earlier row in this cycle was a CREATE for the
  // same entity type, markSynced → remapQueueReferences will have patched our
  // entity_id (and any FK fields in data) in Dexie AFTER pendingFor() cached
  // this row in memory. Without this re-read we'd PATCH /visits/{tempUUID} → 404.
  const freshRaw = await db.sync_queue.get(rec.id);
  if (freshRaw) {
    const freshRec = await unwrapSyncQueuePayload(freshRaw as unknown as Record<string, unknown>);
    // Preserve the "syncing" status we just wrote; only pick up remapped ids/data.
    rec = { ...freshRec, status: "syncing" as const };
  }

  let resp: Response;
  try {
    resp = await postOne(rec, /* force */ false);
  } catch (err) {
    // Left as "syncing"; the next cycle to run — in the tab that switched —
    // resets it to pending and pushes it under the right clinic.
    if (err instanceof ClinicScopeChangedError) throw err;
    return await onTransportError(rec, err);
  }

  if (resp.ok) {
    let responseBody: unknown;
    try { responseBody = await resp.json(); } catch { /* non-json response is fine */ }
    await markSynced(rec, responseBody);
    return "synced";
  }

  // 404 on a delete means the server already removed it — treat as success.
  if (resp.status === 404 && rec.operation === "delete") {
    await markSynced(rec, undefined);
    return "synced";
  }

  if (resp.status === 409) {
    const data = await readConflictBody(resp);
    await db.sync_queue.update(rec.id, {
      status:          "conflict",
      server_snapshot: data?.server_data,
      last_error:      data?.error ?? "Conflict",
    });
    logger.warn("sync-push: conflict", { entity_type: rec.entity_type });
    notify();
    return "conflict";
  }

  if (resp.status >= 400 && resp.status < 500) {
    let errMsg = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      errMsg = body?.error?.message ?? body?.data?.error ?? body?.error ?? errMsg;
    } catch { /* keep default */ }
    await db.sync_queue.update(rec.id, { status: "failed", last_error: errMsg });
    logger.warn("sync-push: client error", { entity_type: rec.entity_type, status: resp.status });
    notify();
    return "failed";
  }

  // 5xx — transient: requeue with retry budget.
  return await onTransportError(rec, new Error(`HTTP ${resp.status}`));
}

/**
 * Mark the queue row done AND flip the corresponding entity table row to
 * sync_status='synced'.
 *
 * For CREATE operations the server generates its own UUID, which differs from
 * the temp UUID we stored locally. We extract the server ID from the response,
 * then remap the Dexie record (delete + re-insert) and patch any still-pending
 * queue entries that reference the old temp ID so child pushes (e.g. an
 * appointment whose patient_id was a temp UUID) use the real server ID.
 */
async function markSynced(rec: SyncQueueRecord, responseBody?: unknown): Promise<void> {
  await db.sync_queue.update(rec.id, { status: "done", last_error: undefined });

  const tempId = rec.entity_id;
  if (!tempId) { notify(); return; }

  const table = entityTableFor(rec.entity_type);

  // For DELETEs: the local row was already removed optimistically; this is a
  // safety cleanup in case anything still exists (e.g. a mid-flight retry).
  if (rec.operation === "delete") {
    if (table) {
      try { await table.delete(tempId); } catch { /* already gone */ }
    }
    notify();
    return;
  }

  // For CREATEs: extract server-assigned ID and remap if it differs.
  if (rec.operation === "create" && responseBody) {
    const serverId = extractServerId(rec.entity_type, responseBody);
    if (serverId && serverId !== tempId) {
      if (table) {
        try {
          const existing = await table.get(tempId);
          if (existing) {
            const idField = entityIdField(rec.entity_type);
            await table.delete(tempId);
            await table.put({
              ...existing,
              [idField]:        serverId,
              sync_status:      "synced",
              local_updated_at: new Date().toISOString(),
            });
          }
        } catch { /* non-fatal — row may have been deleted */ }
      }
      // Patch all pending queue entries whose data references the old temp ID.
      await remapQueueReferences(rec.entity_type, tempId, serverId);
      // Notify any mounted page (e.g. NinetysDiagnosis) that holds the old
      // temp UUID in its local state so it can update before the next save.
      if (rec.entity_type === "visit") {
        window.dispatchEvent(new CustomEvent("kaagazy:visit-remapped", {
          detail: { tempId, serverId },
        }));
      }
      notify();
      return;
    }
  }

  // Default path (UPDATEs, or CREATEs where the ID didn't change).
  if (table) {
    try {
      await table.update(tempId, {
        sync_status:      "synced",
        local_updated_at: new Date().toISOString(),
      });
    } catch { /* row deleted or entity has no local table */ }
  }
  notify();
}

/** Extract the server-assigned primary ID from a successful POST response body. */
function extractServerId(type: EntityType, body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const data = (body as Record<string, unknown>).data as Record<string, unknown> | undefined;
  if (!data || typeof data !== "object") return undefined;

  // POST /visits returns VisitWithVitalsResponse: { visit: {...}, vitals: {...} }
  // The visit_id is nested one level deeper than all other entity types.
  if (type === "visit") {
    const nested = data["visit"] as Record<string, unknown> | undefined;
    const id = nested?.["visit_id"];
    return typeof id === "string" ? id : undefined;
  }

  const id = data[entityIdField(type)];
  return typeof id === "string" ? id : undefined;
}

/** Field name used as primary key for each entity type. */
function entityIdField(type: EntityType): string {
  switch (type) {
    case "patient":      return "patient_id";
    case "allergy":      return "allergy_id";
    case "appointment":  return "appointment_id";
    case "visit":        return "visit_id";
    case "vitals":       return "vitals_id";
    case "prescription": return "prescription_id";
    case "doctor":       return "doctor_id";
  }
}

/**
 * After a CREATE syncs with a new server ID, update all still-pending queue
 * entries that reference the old temp ID — two cases:
 *
 *   1. Same-entity UPDATE/DELETE: `entry.entity_type === syncedType` and
 *      `entry.entity_id === tempId`. The queue row's entity_id is the PATCH
 *      URL parameter, so it must be updated to the server ID or every
 *      subsequent PATCH goes to /visits/{tempId} → 404.
 *
 *   2. FK reference in a child entity: e.g. `prescription.visit_id === tempId`
 *      after a visit CREATE. The FK inside the encrypted payload is patched
 *      to the server ID so the child push doesn't FK-violate.
 */
async function remapQueueReferences(
  syncedType: EntityType,
  tempId: string,
  serverId: string,
): Promise<void> {
  const pending = await db.sync_queue
    .where("status").anyOf("pending", "failed")
    .toArray();

  const updates = await Promise.all(pending.map(async entry => {
    const decrypted = await unwrapSyncQueuePayload(entry as unknown as Record<string, unknown>);
    const data = decrypted.data as Record<string, unknown>;
    let updatedData = { ...data };
    let entityIdChanged = false;

    // Case 1 — same-entity UPDATE/DELETE: remap entity_id and the PK field in data.
    if (entry.entity_type === syncedType && entry.entity_id === tempId) {
      updatedData[entityIdField(syncedType)] = serverId;
      entityIdChanged = true;
    }

    // Case 2 — FK in a child entity: patch any FK field whose value is tempId.
    const refs = FK_REFS[entry.entity_type] ?? [];
    const fkMatches = refs.filter(r => r.refType === syncedType && data[r.field] === tempId);
    fkMatches.forEach(r => { updatedData[r.field] = serverId; });

    if (!entityIdChanged && fkMatches.length === 0) return null;

    const rewrapped = await wrapSyncQueuePayload({ ...decrypted, data: updatedData });
    const patch: Record<string, unknown> = { encrypted_data: rewrapped.encrypted_data };
    if (entityIdChanged) patch.entity_id = serverId;
    return db.sync_queue.update(entry.id, patch);
  }));

  await Promise.all(updates.filter(Boolean));
}

async function onTransportError(rec: SyncQueueRecord, err: unknown): Promise<PushOutcome> {
  const retry_count = rec.retry_count + 1;
  if (retry_count >= SYNC_MAX_RETRIES) {
    await db.sync_queue.update(rec.id, {
      status:      "failed",
      retry_count,
      last_error:  "Max retries exceeded",
    });
    logger.warn("sync-push: retries exhausted", { entity_type: rec.entity_type });
    notify();
    return "failed";
  }
  await db.sync_queue.update(rec.id, {
    status:      "pending",
    retry_count,
    last_error:  err instanceof Error ? err.name : "TransportError",
  });
  notify();
  return "retry";
}

async function readConflictBody(resp: Response): Promise<ServerConflict | null> {
  try {
    const body = await resp.json();
    if (body && typeof body === "object") {
      // Server wraps conflict detail under "detail": { error, server_version, server_data }
      if ("detail" in body && body.detail) return body.detail as ServerConflict;
      // Fallback for any other shape the server might use.
      if ("data" in body && body.data)     return body.data   as ServerConflict;
      return body as ServerConflict;
    }
  } catch { /* malformed conflict — caller treats as bare conflict */ }
  return null;
}

async function postOne(rec: SyncQueueRecord, force: boolean): Promise<Response> {
  const { url, method } = entityRouteFor(rec);
  const isDelete = method === "DELETE";
  const d = (rec.data ?? {}) as Record<string, unknown>;
  const body: Record<string, unknown> = { ...d };
  if (force) body.force = true;

  return apiFetch(url, {
    method,
    headers: !isDelete ? { "Content-Type": "application/json" } : {},
    body: isDelete ? undefined : JSON.stringify(body),
    signal: AbortSignal.timeout(SYNC_FETCH_TIMEOUT_MS),
  });
}

function entityRouteFor(rec: SyncQueueRecord): { url: string; method: string } {
  const base = API_BASE_URL;
  const d = (rec.data ?? {}) as Record<string, unknown>;
  const id = rec.entity_id ?? "";
  const isCreate = rec.operation === "create";

  switch (rec.entity_type) {
    case "patient":
      if (rec.operation === "delete") return { url: `${base}/patients/${id}`, method: "DELETE" };
      return isCreate
        ? { url: `${base}/patients`,       method: "POST"  }
        : { url: `${base}/patients/${id}`, method: "PATCH" };

    case "allergy": {
      const pid = String(d.patient_id ?? "");
      if (rec.operation === "delete") return { url: `${base}/patients/${pid}/allergies/${id}?confirm=true`, method: "DELETE" };
      return isCreate
        ? { url: `${base}/patients/${pid}/allergies`,         method: "POST"  }
        : { url: `${base}/patients/${pid}/allergies/${id}`,   method: "PATCH" };
    }

    case "appointment":
      return isCreate
        ? { url: `${base}/appointments`,     method: "POST"  }
        : { url: `${base}/appointments/${id}`, method: "PATCH" };

    case "visit":
      return isCreate
        ? { url: `${base}/visits`,     method: "POST"  }
        : { url: `${base}/visits/${id}`, method: "PATCH" };

    case "prescription": {
      const vid = String(d.visit_id ?? "");
      return isCreate
        ? { url: `${base}/visits/${vid}/prescriptions`,       method: "POST" }
        : { url: `${base}/visits/${vid}/prescriptions/${id}`, method: "PUT"  };
    }

    case "vitals":
      // Vitals have no standalone server endpoint — they ride with the visit.
      // A queued vitals row would be a bug; the 404 marks it failed safely.
      return { url: `${base}/visits/${String(d.visit_id ?? "")}/vitals`, method: "POST" };

    case "doctor":
      // Doctors are server-managed only; should never appear in the queue.
      return { url: `${base}/doctors/${id}`, method: "PATCH" };
  }
}

// Map an EntityType to its Dexie table, or null if no local table exists.
// Doctors are server-managed only today; that's why the schema has no doctors
// table. Drugs are read-only cache, no push from client.
function entityTableFor(type: EntityType): Table<Record<string, unknown>, string> | null {
  // `as any` widens the union of distinct record types to the generic shape
  // used here — same trick `tableFor` in operations.ts uses. Runtime is unchanged.
  switch (type) {
    case "patient":      return db.patients      as unknown as Table<Record<string, unknown>, string>;
    case "visit":        return db.visits        as unknown as Table<Record<string, unknown>, string>;
    case "vitals":       return db.vitals        as unknown as Table<Record<string, unknown>, string>;
    case "prescription": return db.prescriptions as unknown as Table<Record<string, unknown>, string>;
    case "appointment":  return db.appointments  as unknown as Table<Record<string, unknown>, string>;
    case "allergy":      return db.allergies     as unknown as Table<Record<string, unknown>, string>;
    case "doctor":       return null;
  }
}

// Re-export for tests.
export const __testing = {
  doPushCycle, pushRecord, pendingFor, entityTableFor,
  resetDeviceGateRequeue: () => { deviceGateFailuresRequeued = false; },
};

export type { PushOutcome };
